import React, { useState, useEffect, useRef } from "react";
import "./IntakeForms.css";
import { apiClient } from "../services/api";
import {
  FiFile,
  FiTrash2,
  FiDownload,
  FiZap,
  FiUsers,
  FiLink,
  FiCheck,
  FiX,
  FiBarChart,
  FiTarget,
  FiBriefcase,
  FiSearch,
  FiTrendingUp,
  FiTool,
  FiSmartphone,
  FiMonitor,
  FiStar,
  FiCopy,
  FiMail,
  FiActivity,
} from "react-icons/fi";
import FormCodeGenerator from "./FormCodeGenerator";
import LawyerInfoTab from "./LawyerInfoTab";
import "./IntakeForms.css";

interface FormField {
  id: string;
  label: string;
  type:
    | "text"
    | "email"
    | "phone"
    | "date"
    | "textarea"
    | "dropdown"
    | "radio"
    | "file"
    | "group"
    | "education-table"
    | "work-history-table"
    | "family-members-table"
    | "travel-history-table"
    | "address-history-table"
    | "family-details-table"
    | "address-table"
    | "telephone-table"
    | "marriage-information-table"
    | "national-identity-table"
    | "passport-details-table"
    | "usa-pr-card-table"
    | "other-names-table"
    | "previous-countries-table"
    | "application-country-table"
    | "persons-to-visit-table";
  required: boolean;
  options?: string[];
  placeholder?: string;
  fields?: FormField[]; // For group fields
  isCustom?: boolean; // Track if field was added by lawyer
}

interface IntakeForm {
  id: string;
  title: string;
  caseType: string;
  client_name?: string; // Client name for this intake form
  fields: FormField[];
  formId: string;
  isActive: boolean;
  hasDeactivatedSessions?: boolean; // Track if form has deactivated sessions
  createdAt: string;
  submissionsCount: number;
}

// Helper function to determine display status based on filter
const getDisplayStatus = (form: IntakeForm, formStatusFilter: string) => {
  if (formStatusFilter === "inactive") {
    // When filtering by inactive/expired, show forms with deactivated sessions as "EXPIRED"
    if (form.hasDeactivatedSessions) {
      return { status: "EXPIRED", color: "red" };
    }
    // Completely inactive forms
    return { status: "INACTIVE", color: "gray" };
  } else {
    // For active filter, show the actual form status
    return form.isActive
      ? { status: "ACTIVE", color: "green" }
      : { status: "INACTIVE", color: "gray" };
  }
};

interface FormSubmission {
  id: string;
  formId: string;
  clientData?: any; // Legacy field
  submittedAt?: string; // Legacy field
  submitted_at?: string; // New field
  status: "pending" | "reviewed" | "contacted";
  // Backend fields
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  clients?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  intake_forms?: {
    title: string;
    case_type: string;
  };
  case_details?: any; // New field for the latest submission's case details
}

// Standardized form templates for different case types
const STANDARD_FORMS = {
  visitor_visa: {
    title: "Visitor Visa Application Form",
    fields: [
      // Personal Information
      {
        id: "surname",
        label: "Last name",
        type: "text" as const,
        required: true,
        isCustom: false,
      },
      {
        id: "givenName",
        label: "Given Name",
        type: "text" as const,
        required: true,
        isCustom: false,
      },
      {
        id: "email",
        label: "Email",
        type: "email" as const,
        required: true,
        placeholder: "your.email@example.com",
        isCustom: false,
      },
      {
        id: "dateOfBirth",
        label: "Date of birth",
        type: "date" as const,
        required: false,
        placeholder: "yyyy-mm-dd",
        isCustom: false,
      },
      {
        id: "placeOfBirth",
        label: "Place of Birth",
        type: "text" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "countryOfBirth",
        label: "Country of birth",
        type: "text" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "sex",
        label: "Sex",
        type: "radio" as const,
        required: false,
        options: ["Male", "Female", "Other"],
        isCustom: false,
      },
      {
        id: "eyeColor",
        label: "Eye Color",
        type: "text" as const,
        required: false,
        placeholder: "e.g., Brown, Blue, Green",
        isCustom: false,
      },
      {
        id: "height",
        label: "Height",
        type: "text" as const,
        required: false,
        placeholder: "e.g., 5'8\" or 172 cm",
        isCustom: false,
      },
      {
        id: "nationality",
        label: "Nationality",
        type: "text" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "countryOfResidence",
        label: "Country of Residence (currently living)",
        type: "text" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "maritalStatus",
        label: "Marital Status",
        type: "dropdown" as const,
        required: false,
        options: ["Single", "Married", "Divorced", "Widowed", "Common-law"],
        isCustom: false,
      },
      // Marriage Information - Dynamic table
      {
        id: "marriageInformation",
        label: "Marriage Information",
        type: "marriage-information-table" as const,
        required: false,
        isCustom: false,
      },
      // Previous Countries of Residence - Dynamic table
      {
        id: "previousCountriesOfResidence",
        label:
          "Previous Countries of Residence (Have lived more than 6 months in last 10 years)",
        type: "previous-countries-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "applicationCountryDetails",
        label: "Application Country Details",
        type: "application-country-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "address",
        label: "Address Information",
        type: "address-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "telephone",
        label: "Telephone Numbers",
        type: "telephone-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "otherPhone",
        label: "Other no.",
        type: "phone" as const,
        required: false,
        placeholder: "Alternative phone number",
        isCustom: false,
      },
      {
        id: "hasOtherNames",
        label:
          "Other Names Info (Have you ever used another name: nick name, maiden name, Alias etc)",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      // Other Names Details - Conditional table
      {
        id: "otherNamesDetails",
        label: "Other Names Info",
        type: "other-names-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "uciNumber",
        label: "UCI No. (if applicable)",
        type: "text" as const,
        required: false,
        placeholder: "Unique Client Identifier",
        isCustom: false,
      },
      {
        id: "languageCommunication",
        label: "Do you communicate in English/ French",
        type: "dropdown" as const,
        required: false,
        options: ["English", "French", "Both", "Neither"],
        isCustom: false,
      },
      {
        id: "nativeLanguage",
        label: "Native Language",
        type: "text" as const,
        required: false,
        placeholder: "Spanish, Hindi, Punjabi, Mandarin, Arabic, etc.",
        isCustom: false,
      },
      {
        id: "fundsAvailable",
        label: "Funds Available",
        type: "text" as const,
        required: false,
        placeholder: "Amount in CAD or local currency",
        isCustom: false,
      },
      {
        id: "visaRefusal",
        label:
          "Do you have any Visa Refusal if yes give details (with Document)",
        type: "textarea" as const,
        required: false,
        placeholder: "Details of any previous visa refusals",
        isCustom: false,
      },
      {
        id: "hasUsaPrCard",
        label: "Do you have USA/PR card",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      // USA/PR Card Details - Conditional table
      {
        id: "usaPrCardDetails",
        label: "USA/PR Card Details",
        type: "usa-pr-card-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "otherCitizenship",
        label:
          "Do you have other than home country, Citizenship if yes give details (with Document)",
        type: "textarea" as const,
        required: false,
        placeholder: "Details of other citizenships if applicable",
        isCustom: false,
      },
      {
        id: "purposeOfVisit",
        label: "Purpose of Visit",
        type: "dropdown" as const,
        required: false,
        options: [
          "Tourism",
          "Business",
          "Short-Term Studies",
          "Returning Student",
          "Returning Worker",
          "Super Visa: For Parents or Grandparents",
          "Family Visit",
          "Other",
        ],
        isCustom: false,
      },
      {
        id: "stayFrom",
        label: "How long u will stay: From:",
        type: "date" as const,
        required: false,
        placeholder: "yyyy-mm-dd",
        isCustom: false,
      },
      {
        id: "stayTo",
        label: "To:",
        type: "date" as const,
        required: false,
        placeholder: "yyyy-mm-dd",
        isCustom: false,
      },
      {
        id: "personsToVisit",
        label: "Persons or Institutions to Visit",
        type: "persons-to-visit-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "backgroundInformation",
        label: "Background Information",
        type: "textarea" as const,
        required: false,
        placeholder: "Any additional background information",
        isCustom: false,
      },
      // National Identity Information
      {
        id: "hasNationalId",
        label: "Do you have a valid National Identity Number",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      // National Identity Details - Conditional table
      {
        id: "nationalIdentityDetails",
        label: "National Identity Details",
        type: "national-identity-table" as const,
        required: false,
        isCustom: false,
      },
      // Passport Details - Dynamic table
      {
        id: "passportDetails",
        label: "Passport Details",
        type: "passport-details-table" as const,
        required: false,
        isCustom: false,
      },
      // Education After 12th
      {
        id: "education",
        label: "Highest Level of Post Secondary Education",
        type: "education-table" as const,
        required: false,
        isCustom: false,
      },
      // Work History (Last 10 years) - Dynamic table
      {
        id: "workHistory",
        label: "Work History for Last 10 Years",
        type: "work-history-table" as const,
        required: false,
        isCustom: false,
      },
      // Family in Canada - Dynamic table
      {
        id: "familyInCanada",
        label: "Family Members in Canada",
        type: "family-members-table" as const,
        required: false,
        isCustom: false,
      },
      // Travel History - Dynamic table
      {
        id: "travelHistory",
        label: "Travel History for last 10 years",
        type: "travel-history-table" as const,
        required: false,
        isCustom: false,
      },
      // Address History - Dynamic table
      {
        id: "addressHistory",
        label: "Address History",
        type: "address-history-table" as const,
        required: false,
        isCustom: false,
      },
      // Family Details - Dynamic table
      {
        id: "familyDetails",
        label: "Family Details",
        type: "family-details-table" as const,
        required: false,
        isCustom: false,
      },
      // True/False Questions Section
      {
        id: "politicalPartyMembership",
        label:
          "Are you, or have you ever been a member or associated with any political party, or other group or organization which has engaged in or advocated violence as a means to achieving a political or religious objective, or which has been associated with criminal activity at any time?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "witnessedIllTreatment",
        label:
          "Have you ever witnessed or participated in the ill treatment of prisoners or civilians, looting or desecration of religious buildings?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "criminalOffenses",
        label:
          "Have you ever committed, been arrested for, been charged with or convicted of any criminal offence in any country or territory?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "militaryService",
        label:
          "Did you serve in any military, militia, or civil defence unit or serve in a security organization or police force (including non obligatory national service, reserve or volunteer units)?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "remainedBeyondStatus",
        label:
          "Have you ever remained beyond the validity of your status, attended school without authorization or worked without authorization in Canada?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "visaRefusalHistory",
        label:
          "Have you ever been refused a visa or permit, denied entry or ordered to leave Canada or any other country or territory?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "previousCanadaApplication",
        label: "Have you previously applied to enter or remain in Canada?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "tuberculosisContact",
        label:
          "Within the past two years, have you or a family member ever had tuberculosis of the lungs or been in close contact with a person with tuberculosis?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "healthDisorder",
        label:
          "Do you have any physical or mental disorder that would require social and/or health services, other than medication, during a stay in Canada?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "taiwanPassport",
        label:
          "For this trip, will you use a passport issued by the Ministry of Foreign Affairs in Taiwan that includes your personal identification number?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "israeliPassport",
        label: "For this trip, will you use a National Israeli passport?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
    ],
  },
  study_permit: {
    title: "Study Permit Application Form",
    fields: [
      {
        id: "fullName",
        label: "Full Name",
        type: "text" as const,
        required: true,
        placeholder: "Enter your full name",
        isCustom: false,
      },
      {
        id: "email",
        label: "Email Address",
        type: "email" as const,
        required: true,
        placeholder: "Enter your email address",
        isCustom: false,
      },
      {
        id: "phone",
        label: "Phone Number",
        type: "phone" as const,
        required: true,
        placeholder: "Enter your phone number",
        isCustom: false,
      },
      {
        id: "dateOfBirth",
        label: "Date of Birth",
        type: "date" as const,
        required: true,
        isCustom: false,
      },
      {
        id: "currentAddress",
        label: "Current Address",
        type: "textarea" as const,
        required: true,
        placeholder: "Enter your current address",
        isCustom: false,
      },
      {
        id: "intendedProgram",
        label: "Intended Program of Study",
        type: "text" as const,
        required: true,
        placeholder: "e.g., Bachelor of Computer Science",
        isCustom: false,
      },
      {
        id: "institution",
        label: "Educational Institution",
        type: "text" as const,
        required: true,
        placeholder: "Name of the school/university",
        isCustom: false,
      },
      {
        id: "programDuration",
        label: "Program Duration",
        type: "text" as const,
        required: true,
        placeholder: "e.g., 4 years, 2 semesters",
        isCustom: false,
      },
      {
        id: "fundingSource",
        label: "Funding Source",
        type: "dropdown" as const,
        required: true,
        options: [
          "Personal Funds",
          "Family Support",
          "Scholarship",
          "Bank Loan",
          "Other",
        ],
        isCustom: false,
      },
      {
        id: "passportNumber",
        label: "Passport Number",
        type: "text" as const,
        required: true,
        placeholder: "Enter your passport number",
        isCustom: false,
      },
      {
        id: "additionalNotes",
        label: "Additional Notes",
        type: "textarea" as const,
        required: false,
        placeholder: "Any additional information you would like to share",
        isCustom: false,
      },
    ],
  },
  work_permit: {
    title: "Work Permit Application Form",
    fields: [
      // Personal Information (based on visitor visa but work-focused)
      {
        id: "surname",
        label: "Last name",
        type: "text" as const,
        required: true,
        isCustom: false,
      },
      {
        id: "givenName",
        label: "Given Name",
        type: "text" as const,
        required: true,
        isCustom: false,
      },
      {
        id: "email",
        label: "Email",
        type: "email" as const,
        required: true,
        placeholder: "your.email@example.com",
        isCustom: false,
      },
      {
        id: "dateOfBirth",
        label: "Date of birth",
        type: "date" as const,
        required: false,
        placeholder: "yyyy-mm-dd",
        isCustom: false,
      },
      {
        id: "placeOfBirth",
        label: "Place of Birth",
        type: "text" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "countryOfBirth",
        label: "Country of birth",
        type: "text" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "sex",
        label: "Sex",
        type: "radio" as const,
        required: false,
        options: ["Male", "Female", "Other"],
        isCustom: false,
      },
      {
        id: "nationality",
        label: "Nationality",
        type: "text" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "countryOfResidence",
        label: "Country of Residence (currently living)",
        type: "text" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "maritalStatus",
        label: "Marital Status",
        type: "dropdown" as const,
        required: false,
        options: ["Single", "Married", "Divorced", "Widowed", "Common-law"],
        isCustom: false,
      },
      // Marriage Information - Dynamic table
      {
        id: "marriageInformation",
        label: "Marriage Information",
        type: "marriage-information-table" as const,
        required: false,
        isCustom: false,
      },
      // Previous Countries of Residence - Dynamic table
      {
        id: "previousCountriesOfResidence",
        label:
          "Previous Countries of Residence (Have lived more than 6 months in last 5 years)",
        type: "previous-countries-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "applicationCountryDetails",
        label: "Application Country Details",
        type: "application-country-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "address",
        label: "Address Information",
        type: "address-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "telephone",
        label: "Telephone Information",
        type: "telephone-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "hasOtherNames",
        label:
          "Other Names Info (Have you ever used another name: nick name, maiden name, Alias etc)",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      // Other Names Details - Conditional table
      {
        id: "otherNames",
        label: "Other Names Details",
        type: "other-names-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "languageOfCommunication",
        label: "Do you communicate in English/ French",
        type: "dropdown" as const,
        required: false,
        options: ["English", "French", "Both", "Neither"],
        isCustom: false,
      },
      {
        id: "passportDetails",
        label: "Passport Details",
        type: "passport-details-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "hasUsaPrCard",
        label: "Do you have USA/PR card",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      // USA/PR Card Details - Conditional table
      {
        id: "usaPrCard",
        label: "USA/PR Card Details",
        type: "usa-pr-card-table" as const,
        required: false,
        isCustom: false,
      },
      {
        id: "otherCitizenship",
        label:
          "Do you have other than home country, Citizenship if yes give details (with Document)",
        type: "textarea" as const,
        required: false,
        placeholder: "Details of other citizenships if applicable",
        isCustom: false,
      },
      // DETAILS OF INTENDED WORK IN CANADA - New section based on screenshot
      {
        id: "workPermitType",
        label: "What type of work permit are you applying for?",
        type: "dropdown" as const,
        required: false,
        options: [
          "Exemption from Labour Market Impact Assessment",
          "Labour Market Impact Assessment Stream",
          "Open Work Permit",
          "Other",
          "Seasonal Agricultural Workers Program",
          "Start-up Business Class",
        ],
        isCustom: false,
      },
      {
        id: "employerName",
        label: "Name of Employer",
        type: "text" as const,
        required: false,
        placeholder: "Enter employer name",
        isCustom: false,
      },
      {
        id: "employerAddress",
        label: "Employer Address",
        type: "textarea" as const,
        required: false,
        placeholder: "Enter full employer address",
        isCustom: false,
      },
      {
        id: "workProvince",
        label: "Province of Employment",
        type: "dropdown" as const,
        required: false,
        options: [
          "Alberta",
          "British Columbia",
          "Manitoba",
          "New Brunswick",
          "Newfoundland and Labrador",
          "Northwest Territories",
          "Nova Scotia",
          "Nunavut",
          "Ontario",
          "Prince Edward Island",
          "Quebec",
          "Saskatchewan",
          "Yukon",
        ],
        isCustom: false,
      },
      {
        id: "workCityTown",
        label: "City/Town of Employment",
        type: "text" as const,
        required: false,
        placeholder: "Enter city or town",
        isCustom: false,
      },
      {
        id: "workAddress",
        label: "Work Address",
        type: "text" as const,
        required: false,
        placeholder: "Enter work address",
        isCustom: false,
      },
      {
        id: "jobTitle",
        label: "Job Title",
        type: "text" as const,
        required: false,
        placeholder: "Enter job title",
        isCustom: false,
      },
      {
        id: "jobDescription",
        label: "Job Description",
        type: "textarea" as const,
        required: false,
        placeholder: "Describe job duties and responsibilities",
        isCustom: false,
      },
      {
        id: "employmentFromDate",
        label: "Employment Start Date",
        type: "date" as const,
        required: false,
        placeholder: "yyyy-mm-dd",
        isCustom: false,
      },
      {
        id: "employmentToDate",
        label: "Employment End Date",
        type: "date" as const,
        required: false,
        placeholder: "yyyy-mm-dd",
        isCustom: false,
      },
      {
        id: "lmiaNumber",
        label:
          "Labour Market Impact Assessment (LMIA) No. or Offer of Employment (LMIA Exempt) No.",
        type: "text" as const,
        required: false,
        placeholder: "Enter LMIA number if applicable",
        isCustom: false,
      },
      {
        id: "backgroundInformation",
        label: "Background Information",
        type: "textarea" as const,
        required: false,
        placeholder: "Any additional background information",
        isCustom: false,
      },
      // National Identity Information
      {
        id: "hasNationalId",
        label: "Do you have a valid National Identity Number",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      // National Identity Details - Conditional table
      {
        id: "nationalIdentity",
        label: "National Identity Details",
        type: "national-identity-table" as const,
        required: false,
        isCustom: false,
      },
      // Education History
      {
        id: "educationHistory",
        label: "Education History",
        type: "education-table" as const,
        required: false,
        isCustom: false,
      },
      // Work History
      {
        id: "workHistory",
        label: "Work History",
        type: "work-history-table" as const,
        required: false,
        isCustom: false,
      },
      // Family Members
      {
        id: "familyMembers",
        label: "Family Members",
        type: "family-members-table" as const,
        required: false,
        isCustom: false,
      },
      // Travel History
      {
        id: "travelHistory",
        label: "Travel History",
        type: "travel-history-table" as const,
        required: false,
        isCustom: false,
      },
      // Family Details - Dynamic table
      {
        id: "familyDetails",
        label: "Family Details",
        type: "family-details-table" as const,
        required: false,
        isCustom: false,
      },
      // Address History
      {
        id: "addressHistory",
        label: "Address History",
        type: "address-history-table" as const,
        required: false,
        isCustom: false,
      },
      // Background Questions (same as visitor visa)
      {
        id: "politicalPartyMembership",
        label:
          "Are you, or have you ever been a member or associated with any political party, or other group or organization which has engaged in or advocated violence as a means to achieving a political or religious objective, or which has been associated with criminal activity at any time?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "witnessedIllTreatment",
        label:
          "Have you ever witnessed or participated in the ill treatment of prisoners or civilians, looting or desecration of religious buildings?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "criminalOffence",
        label:
          "Have you ever committed, been arrested for, been charged with or convicted of any criminal offence in any country or territory?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "militaryService",
        label:
          "Did you serve in any military, militia, or civil defence unit or serve in a security organization or police force (including non obligatory national service, reserve or volunteer units)?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "immigrationViolations",
        label:
          "Have you ever remained beyond the validity of your status, attended school without authorization or worked without authorization in Canada?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "visaRefusal",
        label:
          "Have you ever been refused a visa or permit, denied entry or ordered to leave Canada or any other country or territory?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "previousApplication",
        label: "Have you previously applied to enter or remain in Canada?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "tuberculosis",
        label:
          "Within the past two years, have you or a family member ever had tuberculosis of the lungs or been in close contact with a person with tuberculosis?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "physicalMentalDisorder",
        label:
          "Do you have any physical or mental disorder that would require social and/or health services, other than medication, during a stay in Canada?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "taiwanPassport",
        label:
          "For this trip, will you use a passport issued by the Ministry of Foreign Affairs in Taiwan that includes your personal identification number?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
      {
        id: "israeliPassport",
        label: "For this trip, will you use a National Israeli passport?",
        type: "radio" as const,
        required: false,
        options: ["Yes", "No"],
        isCustom: false,
      },
    ],
  },
  permanent_residence: {
    title: "Permanent Residence Application Form",
    fields: [
      {
        id: "fullName",
        label: "Full Name",
        type: "text" as const,
        required: true,
        placeholder: "Enter your full name",
        isCustom: false,
      },
      {
        id: "email",
        label: "Email Address",
        type: "email" as const,
        required: true,
        placeholder: "Enter your email address",
        isCustom: false,
      },
      {
        id: "phone",
        label: "Phone Number",
        type: "phone" as const,
        required: true,
        placeholder: "Enter your phone number",
        isCustom: false,
      },
      {
        id: "dateOfBirth",
        label: "Date of Birth",
        type: "date" as const,
        required: true,
        isCustom: false,
      },
      {
        id: "currentAddress",
        label: "Current Address",
        type: "textarea" as const,
        required: true,
        placeholder: "Enter your current address",
        isCustom: false,
      },
      {
        id: "immigrationProgram",
        label: "Immigration Program",
        type: "dropdown" as const,
        required: true,
        options: [
          "Express Entry",
          "Provincial Nominee Program",
          "Family Sponsorship",
          "Refugee",
          "Other",
        ],
        isCustom: false,
      },
      {
        id: "currentStatus",
        label: "Current Immigration Status",
        type: "dropdown" as const,
        required: true,
        options: ["Visitor", "Student", "Worker", "Refugee", "Other"],
        isCustom: false,
      },
      {
        id: "passportNumber",
        label: "Passport Number",
        type: "text" as const,
        required: true,
        placeholder: "Enter your passport number",
        isCustom: false,
      },
      {
        id: "additionalNotes",
        label: "Additional Notes",
        type: "textarea" as const,
        required: false,
        placeholder: "Any additional information you would like to share",
        isCustom: false,
      },
    ],
  },
};

export default function IntakeForms() {
  const [forms, setForms] = useState<IntakeForm[]>([]);
  const [formData, setFormData] = useState({
    title: "",
    caseType: "visitor_visa",
    country: "Canada", // Default to Canada
    clientName: "", // Client name for the form
    clientEmail: "", // Client email for the form
    fields: STANDARD_FORMS.visitor_visa.fields as FormField[],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [selectedForm, setSelectedForm] = useState<IntakeForm | null>(null);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
  const [selectedSubmission, setSelectedSubmission] =
    useState<FormSubmission | null>(null);
  const [activeTab, setActiveTab] = useState("create-share");

  // New state for combined workflow

  const [codeExpiry, setCodeExpiry] = useState("30"); // days
  const [generatedFormData, setGeneratedFormData] = useState<{
    form: IntakeForm;
    code: string;
    url: string;
  } | null>(null);
  const [showCreateSuccess, setShowCreateSuccess] = useState(false);

  // User filtering for Manage tab
  const [selectedManageUser, setSelectedManageUser] = useState("");
  const [uniqueManageUsers, setUniqueManageUsers] = useState<string[]>([]);
  const [formStatusFilter, setFormStatusFilter] = useState("active"); // "active", "inactive", "all"
  
  // Search and slider functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [latestCount, setLatestCount] = useState(10); // default show 10 latest forms

  // Key to force FormCodeGenerator refresh when forms are created
  const [formCodeGeneratorKey, setFormCodeGeneratorKey] = useState(0);

  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      // await navigator.clipboard.writeText(generatedFormData.url);
      setCopied(true);
      // setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy!", err);
    }
  };

  useEffect(() => {
    loadForms();
    loadSubmissions();
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showFormBuilder) {
        setShowFormBuilder(false);
      }
    };

    if (showFormBuilder) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showFormBuilder]);

  // Load standardized form when case type changes
  useEffect(() => {
    if (
      formData.caseType &&
      STANDARD_FORMS[formData.caseType as keyof typeof STANDARD_FORMS]
    ) {
      const standardForm =
        STANDARD_FORMS[formData.caseType as keyof typeof STANDARD_FORMS];
      
      // Only update if fields are actually different to prevent unnecessary re-renders
      setFormData((prev) => {
        const fieldsChanged = JSON.stringify(prev.fields) !== JSON.stringify(standardForm.fields);
        if (!fieldsChanged) {
          return prev; // Return same reference to prevent re-render
        }
        return {
        ...prev,
          // Don't set title automatically - let user type their own
          fields: [...standardForm.fields], // Copy standard fields
        };
      });
    }
  }, [formData.caseType]);

  const loadForms = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getIntakeForms();
      console.log("Raw API response:", response);

      // Also load form sessions to check actual session status
      const sessionsResponse = await apiClient.getFormSessions();
      console.log("Sessions response for status check:", sessionsResponse);

      let allSessions: any[] = [];
      if (Array.isArray(sessionsResponse)) {
        allSessions = sessionsResponse;
      } else if (sessionsResponse && sessionsResponse.sessions) {
        allSessions = sessionsResponse.sessions;
      }
      
      // Transform backend field names to frontend field names
      const transformedForms = response.map((form: any) => {
        // Get all sessions for this form
        const formSessions = allSessions.filter((session: any) => {
          const sessionFormId = session.formId || session.form_id;
          return sessionFormId === form.id;
        });

        // Check if this form has any active sessions
        const hasActiveSessions = formSessions.some((session: any) => {
          const isSessionActive = session.isActive !== false;
          return isSessionActive;
        });

        // Check if this form has any deactivated sessions
        const hasDeactivatedSessions = formSessions.some((session: any) => {
          return session.isActive === false;
        });

        console.log(
          `Form "${form.title}" has active sessions: ${hasActiveSessions}, deactivated sessions: ${hasDeactivatedSessions}`
        );

        return {
        id: form.id,
        title: form.title,
        caseType: form.case_type,
          client_name: form.client_name, // Include client_name field
        fields: form.fields,
        formId: form.form_id, // Transform form_id to formId
          isActive: hasActiveSessions, // Use session-based status instead of form template status
          hasDeactivatedSessions: hasDeactivatedSessions, // Track if form has deactivated sessions
        createdAt: form.created_at,
          submissionsCount: form.submissionsCount || 0,
        };
      });

      console.log(
        "Transformed forms with session-based status:",
        transformedForms
      );

      // Extract unique users from client_name field
      const users = new Set<string>();
      transformedForms.forEach((form: IntakeForm) => {
        console.log(
          "📝 Processing manage form:",
          form.title,
          "Client:",
          form.client_name
        );
        if (form.client_name && form.client_name.trim().length > 0) {
          const userName = form.client_name.trim();
          console.log("👤 Using client name:", userName);
          users.add(userName);
        } else {
          console.log("⚠️ Form has no client name, skipping:", form.title);
        }
      });

      const uniqueUsersList = Array.from(users).sort();
      console.log("👥 Unique manage users found:", uniqueUsersList);
      setUniqueManageUsers(uniqueUsersList);
      setForms(transformedForms);
    } catch (error) {
      console.error("Error loading forms:", error);
      setError("Failed to load forms");
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    try {
      console.log("Loading submissions...");
      const response = await apiClient.getIntakeFormSubmissions();
      console.log("Submissions API response:", response);
      setSubmissions(response);
    } catch (error) {
      console.error("Error loading submissions:", error);
    }
  };

  const addCustomField = () => {
    const newField: FormField = {
      id: `custom_field_${Date.now()}`,
      label: "",
      type: "text",
      required: false,
      placeholder: "",
      isCustom: true,
    };
    setFormData({
      ...formData,
      fields: [...formData.fields, newField],
    });
  };

  const updateField = (index: number, field: Partial<FormField>) => {
    const updatedFields = [...formData.fields];
    updatedFields[index] = { ...updatedFields[index], ...field };
    setFormData({
      ...formData,
      fields: updatedFields,
    });
  };

  const removeField = (index: number) => {
    const updatedFields = (formData.fields || []).filter((_, i) => i !== index);
    setFormData({ ...formData, fields: updatedFields });
  };

  const refreshFormTemplate = () => {
    // Reset form data to use the latest template
    setFormData({
      title: "",
      caseType: "visitor_visa",
      country: "Canada",
      clientName: "",
      clientEmail: "",
      fields: STANDARD_FORMS.visitor_visa.fields as FormField[],
    });
  };

  const createForm = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Ensure we're using the latest template
      const currentFormData = {
        ...formData,
        fields: STANDARD_FORMS[formData.caseType as keyof typeof STANDARD_FORMS]
          .fields as FormField[],
      };

      console.log("Creating form with data:", currentFormData);
      const response = await apiClient.createIntakeForm(currentFormData);
      console.log("Form creation response:", response);

      // Generate access code immediately after form creation
      try {
        console.log("Generating access code for form:", response.id);
        const codeResponse = await apiClient.generateFormCode(response.id);

        if (codeResponse.success) {
          setGeneratedFormData({
            form: response,
            code: codeResponse.code,
            url: codeResponse.formUrl,
          });

          if (formData.clientEmail) {
            console.log(`📧 Scheduling email to ${formData.clientEmail}`);
            apiClient.sendIntakeFormEmail(response.id, {
              clientEmail: formData.clientEmail,
              clientName: formData.clientName,
              formTitle: formData.title,
              formUrl: codeResponse.formUrl,
            }).catch(emailError => {
              // Log the error but don't block the user's success message
              console.error("Failed to send intake form email:", emailError);
            });
          }

          // Copy URL to clipboard
          navigator.clipboard.writeText(codeResponse.formUrl);

          setShowCreateSuccess(true);
        } else {
          setSuccess("Form created successfully!");
          setTimeout(() => setSuccess(null), 3000);
        }
      } catch (codeError) {
        console.error("Error generating code:", codeError);
        setError(
          "Form created successfully, but failed to generate access code. You can generate it manually from the Manage Existing Forms section."
        );
      }

      setShowFormBuilder(false);
      setFormData({
        title: "",
        caseType: "visitor_visa",
        country: "Canada",
        clientName: "",
        clientEmail: "",
        fields: STANDARD_FORMS.visitor_visa.fields as FormField[],
      });
      loadForms();
      
      // Force FormCodeGenerator to refresh by changing its key
      setFormCodeGeneratorKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error creating form:", error);
      setError("Failed to create form");
    } finally {
      setLoading(false);
    }
  };

  const copyFormLink = async (formId: string) => {
    // This function is deprecated - form access should be managed through Generate Codes
    // Keeping the function for backward compatibility but it's no longer used
    console.warn(
      "Direct form links are deprecated. Use Generate Codes for form access."
    );
  };

  const getCaseTypeLabel = (caseType: string) => {
    const labels: { [key: string]: string } = {
      visitor_visa: "Visitor Visa",
      study_permit: "Study Permit",
      work_permit: "Work Permit",
      permanent_residence: "Permanent Residence",
      citizenship: "Citizenship",
    };
    return labels[caseType] || caseType;
  };

  const getFieldTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      text: "Text Input",
      email: "Email",
      phone: "Phone Number",
      date: "Date",
      textarea: "Long Text",
      dropdown: "Dropdown",
      file: "File Upload",
    };
    return labels[type] || type;
  };

  const viewSubmissions = async (formId: string) => {
    try {
      setLoading(true);
      const response = await apiClient.getIntakeFormSubmissionsByFormId(formId);
      console.log("Submissions for form", formId, ":", response);
      
      // Ensure response is an array
      const submissions = Array.isArray(response) ? response : [];
      
      if (submissions.length > 0) {
        // Set the submissions data and show the submissions modal
        setSubmissions(submissions);
        setShowSubmissions(true);
      } else {
        setSubmissions([]);
        setShowSubmissions(true);
      }
    } catch (error) {
      console.error("Error loading submissions for form", formId, ":", error);
      setError("Failed to load submissions for this form");
    } finally {
      setLoading(false);
    }
  };

  const viewSubmissionDetails = (submission: FormSubmission) => {
    // TODO: Implement detailed view of submission data
    console.log("Viewing submission details:", submission);
    alert("Submission details view coming soon!");
  };

  const createCaseFromSubmission = (submission: FormSubmission) => {
    // TODO: Implement case creation from submission
    console.log("Creating case from submission:", submission);
    alert("Case creation from submission coming soon!");
  };

  const openSubmissionForm = async (submission: FormSubmission) => {
    try {
      // Show the submission details in a modal
      setSelectedSubmission(submission);
      setShowSubmissionDetails(true);
      setShowSubmissions(false);
    } catch (error) {
      console.error("Error opening submission form:", error);
      setError("Failed to open submission form.");
    }
  };

  const formatFieldName = (key: string) => {
    // This function is needed to handle nested keys like 'passport_details.passport_number'
    // and display them as 'Passport Details.Passport Number'
    const parts = key.split(".");
    return parts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const renderFieldValue = (value: any) => {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      // Check if this is a dynamic table object (old format with numbered keys)
      const keys = Object.keys(value);
      const isNumberedObject = keys.some((key) => /^\w+\d+$/.test(key)); // e.g., workHistory1, familyDetails2
      
      if (isNumberedObject) {
        // Handle old format: numbered objects like workHistory1, workHistory2
        return (
          <div className="field-value-object">
            {Object.entries(value).map(([subKey, subValue]) => {
              // Skip radio button fields (ending with Yes/No)
              if (subKey.endsWith("Yes") || subKey.endsWith("No")) {
                return null;
              }
              return (
                <div key={subKey} className="field-value-item">
                  <strong>{formatFieldName(subKey)}:</strong>{" "}
                  {renderFieldValue(subValue)}
                </div>
              );
            })}
          </div>
        );
      } else {
        // Handle regular objects
        return (
          <div className="field-value-object">
            {Object.entries(value).map(([subKey, subValue]) => (
              <div key={subKey} className="field-value-item">
                <strong>{formatFieldName(subKey)}:</strong>{" "}
                {renderFieldValue(subValue)}
              </div>
            ))}
          </div>
        );
      }
    } else if (Array.isArray(value)) {
      // Handle new format: arrays with objects containing IDs
      if (value.length === 0) {
        return "No entries";
      }
      
      return (
        <div className="field-value-array">
          {value.map((item, index) => {
            if (typeof item === "object" && item !== null) {
              return (
                <div key={index} className="field-value-item">
                  <strong>Entry {index + 1}:</strong>
                  <div className="field-value-nested">
                    {Object.entries(item).map(([itemKey, itemValue]) => {
                      // Skip the ID field as it's not user data
                      if (itemKey === "id") {
                        return null;
                      }
                      return (
                        <div key={itemKey} className="field-value-nested-item">
                          <strong>{formatFieldName(itemKey)}:</strong>{" "}
                          {String(itemValue || "Not provided")}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            } else {
              return (
                <div key={index} className="field-value-item">
                  {String(item)}
                </div>
              );
            }
          })}
        </div>
      );
    } else {
      return String(value || "Not provided");
    }
  };

  return (
    <div
      className="intake-forms-container"
      style={{
        overflowY: "scroll",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <div
        style={{
          background:
            "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 50%, rgba(147, 51, 234, 0.1) 100%)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(139, 92, 246, 0.2)",
          padding: "20px 24px",
          borderRadius: "16px",
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: "0",
          zIndex: "100",
          maxWidth: "1200px",
          width: "100%",
          boxSizing: "border-box",
          height: "80px",
          minHeight: "80px",
        }}
      >
        <h1
          style={{
            fontSize: "28px",
            margin: "0",
            color: "#000000",
            fontWeight: "700",
          }}
        >
          Intake Forms
        </h1>
        <div className="intake-forms-actions">
          <button 
            className={`tab-btn ${
              activeTab === "create-share" ? "active" : ""
            }`}
            onClick={() => setActiveTab("create-share")}
            style={{
              padding: "12px 24px",
              background:
                activeTab === "create-share"
                  ? "linear-gradient(135deg, #10b981, #059669)"
                  : "rgba(255, 255, 255, 0.1)",
              color: activeTab === "create-share" ? "white" : "#374151",
              border:
                activeTab === "create-share"
                  ? "none"
                  : "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "12px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
              boxShadow:
                activeTab === "create-share"
                  ? "0 4px 16px rgba(16, 185, 129, 0.3)"
                  : "0 2px 8px rgba(0, 0, 0, 0.04)",
              backdropFilter: "blur(10px)",
            }}
          >
            Create & Share
          </button>
          <button 
            className={`tab-btn ${activeTab === "manage" ? "active" : ""}`}
            onClick={() => setActiveTab("manage")}
            style={{
              padding: "12px 24px",
              background:
                activeTab === "manage"
                  ? "linear-gradient(135deg, #10b981, #059669)"
                  : "rgba(255, 255, 255, 0.1)",
              color: activeTab === "manage" ? "white" : "#374151",
              border:
                activeTab === "manage"
                  ? "none"
                  : "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "12px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
              boxShadow:
                activeTab === "manage"
                  ? "0 4px 16px rgba(16, 185, 129, 0.3)"
                  : "0 2px 8px rgba(0, 0, 0, 0.04)",
              backdropFilter: "blur(10px)",
            }}
          >
            {React.createElement(FiFile as any, { size: 16 })}
            Manage Forms
          </button>
        
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {activeTab === "create-share" && (
        <div className="create-share-tab">
          <div className="create-share-header">
            <h2>Create New Form & Generate Access Code</h2>
            <p className="tab-description">
              Create a new intake form and automatically generate a secure
              access code to share with your client.
            </p>
        </div>

          <div className="create-share-actions"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}>
            <button 
              className="create-form-btn primary-action"
              onClick={() => setShowFormBuilder(true)}
              style={{
                background: "linear-gradient(135deg, #28a745, #1e7e34)",
                color: "white",
                border: "2px solid #28a745",
                fontWeight: "700",
                fontSize: "16px",
                boxShadow: "0 2px 8px rgba(40, 167, 69, 0.2)",
                padding: "16px 32px",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "linear-gradient(135deg, #218838, #155724)";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 15px rgba(40, 167, 69, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "linear-gradient(135deg, #28a745, #1e7e34)";
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow =
                  "0 2px 8px rgba(40, 167, 69, 0.2)";
              }}
            >
              {React.createElement(FiZap as any, {
                size: 18,
              })}
              Create New Form & Generate Code
            </button>
          </div>

          {/* Active Form Sessions Section */}
          <div
            className="active-sessions-section"
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "16px",
              padding: "24px",
              marginTop: "32px",
            }}
          >
            <h3
              style={{
                color: "#000000",
                fontWeight: "600",
                fontSize: "20px",
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {React.createElement(FiActivity as any, { size: 20 })}
              Active Form Links
            </h3>
            <p
              className="section-description"
              style={{
                color: "#6b7280",
                fontSize: "14px",
                marginBottom: "20px",
                lineHeight: "1.5",
              }}
            >
              Here are your recently generated form links. Copy and share these
              with your clients.
            </p>

            <div className="sessions-container">
              <FormCodeGenerator
                key={formCodeGeneratorKey}
                onCodeGenerated={(code, url) => {
                  setSuccess(`New access code generated! Code: ${code}`);
                  setTimeout(() => setSuccess(null), 5000);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "manage" && (
        <div
          className="manage-tab"
          style={{
            background: "rgba(255, 255, 255, 0.02)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            borderRadius: "16px",
            padding: "24px",
          }}
        >
          <div className="manage-header">
            <h2
              style={{
                color: "#000000",
                fontWeight: "700",
                fontSize: "24px",
                marginBottom: "8px",
              }}
            >
              Manage Existing Forms
            </h2>
            <p
              className="tab-description"
              style={{
                color: "#6b7280",
                fontSize: "14px",
                marginBottom: "20px",
                lineHeight: "1.5",
              }}
            >
              {`Showing ${latestCount} most recent forms. Use the search bar and slider to filter forms.`}
            </p>

            {/* Search Bar */}
            <div className="search-bar-container" style={{ marginBottom: "20px" }}>
              <input
                type="text"
                placeholder="Search by client name or form title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  fontSize: "14px",
                  backgroundColor: "#fff"
                }}
              />
            </div>

            {/* Slider to show latest N forms */}
            <div className="latest-slider-container" style={{ margin: "10px 0 20px 0" }}>
              <label htmlFor="latest-slider" style={{ marginRight: "10px", color: "#000", fontSize: "14px" }}>
                Show latest {latestCount} forms:
              </label>
              <input
                type="range"
                id="latest-slider"
                min={1}
                max={50}
                value={latestCount}
                onChange={(e) => setLatestCount(Number(e.target.value))}
                style={{ verticalAlign: "middle" }}
              />
            </div>

            {/* Status Filter */}
            <div
              className="manage-filter-section"
              style={{
                background: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                padding: "16px",
                marginTop: "16px",
              }}
            >
              <div
                  className="status-filter"
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "12px",
                  }}
                >
                  <label
                    style={{
                      fontWeight: "600",
                      color: "#374151",
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                      minWidth: "110px",
                      textAlign: "left",
                      lineHeight: "1.5",
                    }}
                  >
                    Status:
                  </label>
                  <select
                    value={formStatusFilter}
                    onChange={(e) => {
                      console.log(
                        "🔄 Form status filter changed to:",
                        e.target.value
                      );
                      setFormStatusFilter(e.target.value);
                    }}
                    className="status-select"
                    style={{
                      padding: "8px 12px",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "8px",
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      color: "#374151",
                      fontSize: "14px",
                      minWidth: "150px",
                      cursor: "pointer",
                      lineHeight: "1.5",
                    }}
                  >
                    <option value="active">Active Forms</option>
                    <option value="inactive">Inactive/Expired</option>
                    <option value="all">All Forms</option>
                  </select>
                </div>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading forms...</div>
          ) : forms.length === 0 ? (
            <div className="empty-state">
              <h3>No intake forms yet</h3>
              <p>
                Switch to "Create & Share Forms" tab to create your first form.
              </p>
            </div>
          ) : (
            <div className="forms-section">
              <div className="forms-list">
                {(() => {
                  // Filter forms based on search, status, and slider
                  console.log("🔍 Filtering manage forms with search:", searchQuery);
                  console.log("📊 Total forms:", forms.length);

                  let filteredForms = forms;

                  // First filter by search query
                  if (searchQuery) {
                    const query = searchQuery.toLowerCase();
                    filteredForms = filteredForms.filter((form) => {
                      const clientName = (form.client_name || "").toLowerCase();
                      const formTitle = (form.title || "").toLowerCase();
                      return clientName.includes(query) || formTitle.includes(query);
                    });
                    console.log("✅ Filtered by search:", filteredForms.length);
                  }

                  // Then filter by status
                  console.log("🔍 Filtering by status:", formStatusFilter);
                  if (formStatusFilter === "active") {
                    filteredForms = filteredForms.filter(
                      (form) => form.isActive === true
                    );
                  } else if (formStatusFilter === "inactive") {
                    filteredForms = filteredForms.filter(
                      (form) =>
                        form.isActive === false ||
                        form.hasDeactivatedSessions === true
                    );
                  }
                  // "all" shows both active and inactive, so no additional filtering needed

                  // Sort by creation date (newest first)
                  filteredForms = filteredForms.sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  );

                  // Apply slider limit
                  console.log(`🔢 Applying slider limit: ${latestCount} forms`);
                  filteredForms = filteredForms.slice(0, latestCount);

                  console.log("✅ Final filtered forms:", filteredForms.length);

                  if (filteredForms.length === 0) {
                    const getEmptyStateMessage = () => {
                      if (searchQuery) {
                        return {
                          title: `No forms found for "${searchQuery}"`,
                          description: "Try a different search query.",
                        };
                      }
                      if (formStatusFilter === "active") {
                        return {
                          title: "No active forms found",
                          description: "Create some forms to see them here.",
                        };
                      } else if (formStatusFilter === "inactive") {
                        return {
                          title: "No inactive forms found",
                          description:
                            "No forms have been expired or closed yet.",
                        };
                      } else {
                        return {
                          title: "No forms found",
                          description: "Create some forms to see them here.",
                        };
                      }
                    };

                    const emptyState = getEmptyStateMessage();
                    return (
                      <div className="empty-state">
                        <h3>{emptyState.title}</h3>
                        <p>{emptyState.description}</p>
              </div>
                    );
                  }

                  return filteredForms.map((form) => (
                    <div
                      key={form.id}
                      className="form-row"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        marginBottom: "12px",
                        transition: "all 0.2s ease",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h3
                          style={{
                            color: "#000",
                            fontWeight: "600",
                            fontSize: "16px",
                            margin: "0 0 4px 0",
                          }}
                        >
                          {form.title}
                        </h3>
                        <p style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>
                          {form.client_name ? `Client: ${form.client_name}` : `Case: ${getCaseTypeLabel(form.caseType)}`}
                      </p>
                    </div>
                      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                        <span
                          className={`status ${getDisplayStatus(
                            form,
                            formStatusFilter
                          ).status.toLowerCase()}`}
                          style={{
                            background: getDisplayStatus(form, formStatusFilter)
                              .color,
                            color: "white",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "11px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                          }}
                        >
                          {getDisplayStatus(form, formStatusFilter).status}
                        </span>
                      <button 
                        onClick={() => setSelectedForm(form)}
                          style={{
                            background: "#10b981",
                            border: "none",
                            color: "#fff",
                            padding: "8px 20px",
                            borderRadius: "6px",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Enhanced Form Builder Modal */}
      {showFormBuilder && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h2>Create & Share Intake Form</h2>
              <button 
                className="close-btn"
                onClick={() => setShowFormBuilder(false)}
                title="Close (Esc)"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              {/* <div className="form-creation-steps">
                <div className="step-indicator">
                  <div className="step active">
                    <span className="step-number">1</span>
                    <span className="step-label">Form Details</span>
                  </div>
                  <div className="step-divider"></div>
                  <div className="step active">
                    <span className="step-number">2</span>
                    <span className="step-label">Access Settings</span>
                  </div>
                </div>
              </div> */}

              <div className="form-creation-content">
                {/* Step 1: Form Details */}
                <div className="creation-step">
                  <h3>Step 1: Form Configuration</h3>

              <div className="form-group">
                <label>Case Type *</label>
                <select
                  value={formData.caseType}
                      onChange={(e) =>
                        setFormData({ ...formData, caseType: e.target.value })
                      }
                >
                  <option value="visitor_visa">Visitor Visa</option>
                  <option value="study_permit">Study Permit</option>
                  <option value="work_permit">Work Permit</option>
                      <option value="permanent_residence">
                        Permanent Residence
                      </option>
                  <option value="citizenship">Citizenship</option>
                </select>
                <small className="form-help">
                      Selecting a case type will automatically load standard
                      fields for that application type.
                </small>
              </div>
              
              <div className="form-group">
                    <label>Country *</label>
                    <select
                      value={formData.country}
                      onChange={(e) =>
                        setFormData({ ...formData, country: e.target.value })
                      }
                    >
                      <option value="Canada">Canada</option>
                      <option value="USA">United States</option>
                    </select>
                <small className="form-help">
                      Select the country for which the intake form is intended.
                </small>
              </div>
              
                  <div className="form-group">
                    <label>Client Name *</label>
                        <input
                          type="text"
                      value={formData.clientName}
                      onChange={(e) =>
                        setFormData({ ...formData, clientName: e.target.value })
                      }
                      placeholder="e.g., John Doe"
                      maxLength={20}
                    />
                    <small className="form-help">
                      Enter the client's name for this intake form.
                </small>
              </div>
              
                   <div className="form-group">
                <label>Client Email (to send link)</label>
                          <input
                  type="email"
                  value={formData.clientEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, clientEmail: e.target.value })
                  }
                  placeholder="client.email@example.com"
                />
                <small className="form-help">
                  Optional. If provided, an email with the form link will be sent to the client.
                </small>
                      </div>
                      
                  <div className="form-group">
                    <label>Form Title *</label>
                          <input
                            type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      maxLength={20}
                      placeholder={`e.g., ${
                        formData.caseType === "visitor_visa"
                          ? "Visitor Visa"
                          : formData.caseType === "study_permit"
                          ? "Study Permit"
                          : formData.caseType === "work_permit"
                          ? "Work Permit"
                          : formData.caseType === "permanent_residence"
                          ? "Permanent Residence"
                          : formData.caseType === "citizenship"
                          ? "Citizenship"
                          : "Application"
                      } Application Form - ${
                        formData.clientName || "John Doe"
                      }`}
                    />
                    <small className="form-help">
                      Customize the title for this intake form.
                    </small>
                        </div>
                </div>
                
                {/* Step 2: Access Code Settings */}
                <div className="creation-step">
                  <h3>Step 2: Access Code Settings</h3>

                  <div className="form-group">
                    <label>Code Expiry</label>
                          <select
                      value={codeExpiry}
                      onChange={(e) => setCodeExpiry(e.target.value)}
                    >
                      <option value="7">7 days</option>
                      <option value="30">30 days</option>
                      <option value="60">60 days</option>
                      <option value="90">90 days</option>
                          </select>
                    <small className="form-help">
                      How long the access code will remain valid.
                    </small>
                  </div>
                </div>

                {/* Form Fields Preview */}
                <div className="form-fields-preview">
                  <h3>Form Fields Preview</h3>
                  <div className="fields-summary">
                    <p>
                      This form will include{" "}
                      <strong>{(formData.fields || []).length}</strong> fields
                      based on the {getCaseTypeLabel(formData.caseType)}{" "}
                      template.
                    </p>
                    <small>
                      You can customize fields after creation if needed.
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
                          <button
                className="btn-secondary"
                onClick={() => setShowFormBuilder(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary create-and-share-btn"
                onClick={createForm}
                disabled={
                  loading ||
                  !formData.title ||
                  !formData.country ||
                  !formData.clientName
                }
                style={{
                  background: "linear-gradient(135deg, #28a745, #1e7e34)",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "2px solid #28a745",
                  fontWeight: "700",
                  fontSize: "16px",
                  boxShadow: "0 2px 8px rgba(40, 167, 69, 0.2)",
                  opacity:
                    loading ||
                    !formData.title ||
                    !formData.country ||
                    !formData.clientName
                      ? "0.7"
                      : "1",
                  filter:
                    loading ||
                    !formData.title ||
                    !formData.country ||
                    !formData.clientName
                      ? "brightness(0.85)"
                      : "none",
                }}
              >
                {loading ? "Creating..." : "Create Form & Generate Code"}
                          </button>
                        </div>
          </div>
        </div>
      )}

      {/* Success Modal for Combined Workflow */}
      {showCreateSuccess && generatedFormData && (
        <div className="modal-overlay">
          <div className="modal">
            <div
              className="modal-header success-header"
              style={{
                background: "linear-gradient(135deg, #10b981, #059669)",
                color: "white",
                borderRadius: "12px 12px 0 0",
                padding: "20px",
                position: "relative",
              }}
            >
              <h2
                style={{
                  margin: "0",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {React.createElement(FiCheck as any, { size: 24 })}
                Form Created & Code Generated!
              </h2>
              <button
                onClick={() => {
                  setShowCreateSuccess(false);
                  setGeneratedFormData(null);
                }}
                style={{
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  background: "transparent",
                  border: "none",
                  color: "white",
                  fontSize: "24px",
                  cursor: "pointer",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) =>
                  ((e.target as HTMLButtonElement).style.backgroundColor =
                    "rgba(255,255,255,0.2)")
                }
                onMouseLeave={(e) =>
                  ((e.target as HTMLButtonElement).style.backgroundColor =
                    "transparent")
                }
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="success-content">
                <div className="success-summary">
                  <h3>Your form is ready to share!</h3>
                  <p>
                    Form "<strong>{generatedFormData.form.title}</strong>" has
                    been created and is ready to share.
                  </p>
                </div>

                <div className="generated-details">
                  <div className="detail-group">
                    <label>Shareable Link:</label>
                    <div className="url-display">
                            <input
                              type="text"
                        value={generatedFormData.url}
                        readOnly
                        className="url-input"
                      />
                      <button
                        className="copy-btn"
                        onClick={handleCopy}
                        style={{
                          background: copied ? "#16a34a" : "#3b82f6",
                          color: "white",
                          border: "none",
                          padding: "10px 15px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: "600",
                          transition: "all 0.3s ease",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {copied
                          ? React.createElement(FiCheck as any, { size: 16 })
                          : React.createElement(FiLink as any, { size: 16 })}
                        {copied ? "Copied!" : "Copy Link"}
                      </button>
                          </div>
                      </div>
                </div>

                <div
                  className="next-steps"
                  style={{
                    background: "#fef3c7",
                    border: "1px solid #fbbf24",
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <h4
                    style={{
                      color: "#92400e",
                      margin: "0 0 15px 0",
                    }}
                  >
                    Next Steps:
                  </h4>
                  <ul
                    style={{
                      margin: "0",
                      paddingLeft: "20px",
                      color: "#92400e",
                    }}
                  >
                    <li
                      style={{
                        marginBottom: "8px",
                        lineHeight: "1.5",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {React.createElement(FiCheck as any, {
                        size: 16,
                        color: "#059669",
                      })}
                      Link has been copied to your clipboard
                    </li>
                    <li
                      style={{
                        marginBottom: "8px",
                        lineHeight: "1.5",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {React.createElement(FiMail as any, {
                        size: 16,
                        color: "#3b82f6",
                      })}
                      Send the link to your client via email or messaging
                    </li>
                    <li
                      style={{
                        marginBottom: "8px",
                        lineHeight: "1.5",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {React.createElement(FiSmartphone as any, {
                        size: 16,
                        color: "#8b5cf6",
                      })}
                      Client can access the form using the shareable link{" "}
                    </li>
                    <li
                      style={{
                        marginBottom: "0",
                        lineHeight: "1.5",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {React.createElement(FiBarChart as any, {
                        size: 16,
                        color: "#f59e0b",
                      })}
                      Monitor submissions in the "Manage Existing Forms" tab
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowCreateSuccess(false);
                  setGeneratedFormData(null);
                }}
              >
                Close
              </button>
              <button 
                className="btn-primary"
                onClick={() => {
                  setShowCreateSuccess(false);
                  setGeneratedFormData(null);
                  setActiveTab("manage");
                }}
              >
                View All Forms
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Details Modal */}
      {selectedForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{selectedForm.title}</h2>
              <button 
                className="close-btn"
                onClick={() => setSelectedForm(null)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-info">
                <h3>Form Information</h3>
                <p>
                  <strong>Title:</strong> {selectedForm.title}
                </p>
                <p>
                  <strong>Case Type:</strong>{" "}
                  {getCaseTypeLabel(selectedForm.caseType)}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  {selectedForm.isActive ? "Active" : "Inactive"}
                </p>
                <p>
                  <strong>Created:</strong>{" "}
                  {new Date(selectedForm.createdAt).toLocaleDateString()}
                </p>
                <p>
                  <strong>Submissions:</strong> {selectedForm.submissionsCount}
                </p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setSelectedForm(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submissions Modal */}
      {showSubmissions && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h2>Latest Client Submission</h2>
              <button 
                className="close-btn"
                onClick={() => setShowSubmissions(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {loading ? (
                <div className="loading">Loading submissions...</div>
              ) : submissions.length === 0 ? (
                <div className="empty-state">
                  <h3>No submissions yet</h3>
                  <p>No clients have submitted this form yet.</p>
                </div>
              ) : (
                <div className="latest-submission">
                  {/* Show only the latest submission */}
                  {(() => {
                    const latestSubmission = submissions[0];
                    return (
                      <div className="submission-card">
                        <div className="submission-header">
                          <h4
                            style={{
                              margin: "0",
                              color: "#000000",
                              fontSize: "18px",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {React.createElement(FiFile as any, { size: 18 })}
                            Latest Submission
                          </h4>
                          <span className="submission-date">
                            {new Date(
                              latestSubmission.submitted_at ||
                                latestSubmission.submittedAt ||
                                ""
                            ).toLocaleString()}
                          </span>
                        </div>
                        
                        <div className="submission-body">
                          <p>
                            <strong>Client:</strong>{" "}
                            {latestSubmission.client_name || "Unknown Client"}
                          </p>
                          <p>
                            <strong>Email:</strong>{" "}
                            {latestSubmission.client_email ||
                              "No email provided"}
                          </p>
                          <p>
                            <strong>Phone:</strong>{" "}
                            {latestSubmission.client_phone ||
                              "No phone provided"}
                          </p>
                          <p>
                            <strong>Status:</strong> {latestSubmission.status}
                          </p>
                        </div>
                        
                        {latestSubmission.case_details && (
                          <div className="submission-form-data">
                            <h5>Form Data:</h5>
                            <div className="form-data-display">
                              {Object.entries(
                                latestSubmission.case_details
                              ).map(([key, value]) => {
                                // Skip complex objects and show only simple values
                                if (
                                  typeof value === "object" &&
                                  value !== null
                                ) {
                                  return null;
                                }
                                if (
                                  typeof value === "string" &&
                                  value.trim() === ""
                                ) {
                                  return null;
                                }
                                return (
                                  <div key={key} className="form-data-item">
                                    <strong>{key}:</strong> {String(value)}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        <div className="submission-actions">
                          <button 
                            className="btn-primary"
                            onClick={() => openSubmissionForm(latestSubmission)}
                          >
                            View Full Details
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowSubmissions(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submission Details Modal */}
      {showSubmissionDetails && selectedSubmission && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h2>Client Submission Details</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowSubmissionDetails(false);
                  setSelectedSubmission(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="submission-info">
                <h3>Client Information</h3>
                <p>
                  <strong>Name:</strong>{" "}
                  {selectedSubmission.client_name || "Unknown Client"}
                </p>
                <p>
                  <strong>Email:</strong>{" "}
                  {selectedSubmission.client_email || "No email provided"}
                </p>
                <p>
                  <strong>Phone:</strong>{" "}
                  {selectedSubmission.client_phone || "No phone provided"}
                </p>
                <p>
                  <strong>Submitted:</strong>{" "}
                  {new Date(
                    selectedSubmission.submitted_at ||
                      selectedSubmission.submittedAt ||
                      ""
                  ).toLocaleString()}
                </p>
                <p>
                  <strong>Status:</strong> {selectedSubmission.status}
                </p>
              </div>
              
              {selectedSubmission.case_details && (
                <div className="submission-form-data">
                  <h3>Complete Form Data</h3>
                  <div className="form-data-display">
                    {Object.entries(selectedSubmission.case_details).map(
                      ([key, value]) => {
                      return (
                        <div key={key} className="form-data-item">
                          <strong>{formatFieldName(key)}:</strong>
                          <div className="field-value">
                            {renderFieldValue(value)}
                          </div>
                        </div>
                      );
                      }
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowSubmissionDetails(false);
                  setSelectedSubmission(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 
